/**
 * The run page's right rail (under the stream on a phone): acceptance criteria with live verdicts,
 * the files the branch changed, the landing queue's checks, the mission's sessions, and links to
 * its worktree, session and item. Each section is one panel with a quiet count; nothing is drawn
 * for a value nobody knows yet.
 */
import { Check, Circle, CircleCheck, CircleX, ExternalLink, FileDiff, FolderGit2, Hand, Info, ListChecks, ShieldCheck, SquareArrowOutUpRight, TriangleAlert } from '@orgus/ui/icons';
import type { ComponentType, ReactNode } from 'react';
import { Link } from 'react-router';
import { criterionText } from '@orgus/protocol';
import type { BacklogItem, DiffScope, Landing, LandingEventTone, Mission, MissionSessionView, Session } from '@orgus/protocol';
import { CopyButton, IconButton, Pressable, RelativeTime, Skeleton, cn, formatDuration, formatInt, shortPath } from '@orgus/ui';
import { useDiff } from '../../session-detail/api.ts';
import { SessionStatePill, StatePill } from '../parts.tsx';
import { plainText } from '../../../lib/plainText.ts';

type Icon = ComponentType<{ className?: string }>;

// ---------------------------------------------------------------- criteria

type Verdict = 'met' | 'unmet' | 'said-met' | 'said-unmet' | 'open';

const VERDICT: Record<Verdict, { icon: Icon; fg: string; label: string }> = {
  met: { icon: CircleCheck, fg: 'text-status-success', label: 'Met' },
  unmet: { icon: CircleX, fg: 'text-status-danger', label: 'Not met' },
  'said-met': { icon: CircleCheck, fg: 'text-fg-subtle', label: 'The session reports met' },
  'said-unmet': { icon: CircleX, fg: 'text-fg-subtle', label: 'The session reports not met' },
  open: { icon: Circle, fg: 'text-fg-subtle', label: 'Not met yet' },
};

export function CriteriaPanel({ item, mission }: { item: BacklogItem; mission: Pick<Mission, 'report' | 'closed'> }) {
  const report = mission.report ?? null;
  // Read while blocked (R-HL-3): the session's own account; only a finished mission's report checks criteria off.
  const blocked = !!report?.blocked && !mission.closed;
  const rows = item.acceptance.map((c) => {
    const said = report?.acceptance.find((a) => a.id === c.id) ?? null;
    const v: Verdict = c.done ? 'met' : said ? (blocked ? (said.met ? 'said-met' : 'said-unmet') : said.met ? 'met' : 'unmet') : 'open';
    return { c, said, v };
  });
  const met = rows.filter((r) => r.v === 'met').length;
  // While blocked nothing is checked off: the count says what the session reports, each row its evidence.
  const reported = blocked ? report!.acceptance.filter((a) => a.met).length : 0;
  return (
    <section className="flex flex-col gap-3" aria-label="Acceptance criteria">
      <RailHeading icon={ListChecks} title="Acceptance criteria" count={blocked ? `${reported} of ${rows.length} reported` : `${met} of ${rows.length} met`} />
      <ul className="flex flex-col gap-3">
        {rows.map(({ c, said, v }, i) => {
          const V = VERDICT[v];
          return (
            <li key={c.id} className="flex min-w-0 items-start gap-2">
              <span className={cn('flex h-5 shrink-0 items-center', V.fg)} role="img" aria-label={V.label}>
                <V.icon className="size-4" />
              </span>
              <span className="flex min-w-0 flex-col gap-1">
                <span className={cn('text-ui', v === 'met' ? 'text-fg-muted' : 'text-fg')}>
                  <span className="mr-1 text-caption text-fg-subtle num">AC-{i + 1}</span>
                  {criterionText(c)}
                </span>
                {said && (
                  <span className="line-clamp-2 text-caption text-fg-subtle">
                    {said.evidence ? said.evidence.charAt(0).toUpperCase() + said.evidence.slice(1) : V.label}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------- shared

function RailHeading({ icon: I, title, count, action }: { icon: Icon; title: string; count?: string; action?: ReactNode }) {
  return (
    <div className="flex h-6 min-w-0 items-center gap-2">
      <I className="size-4 shrink-0 text-fg-subtle" aria-hidden />
      <h3 className="truncate text-ui font-medium text-fg">{title}</h3>
      {count && <span className="truncate text-caption text-fg-subtle num">{count}</span>}
      {action && <span className="ml-auto flex shrink-0 items-center">{action}</span>}
    </div>
  );
}

// ---------------------------------------------------------------- files changed

const TOP_FILES = 5;

/**
 * The line over the file list, or the empty line. QA-14: a branch with no commits yet falls back to
 * the folder's uncommitted changes (scope working), so what the session left unsaved is still seen.
 */
export function changesLine(scope: DiffScope, files: number, uncommitted: boolean): string | null {
  if (uncommitted) return files > 0 ? `${formatInt(files)} ${files === 1 ? 'file' : 'files'} changed, not committed` : 'No commits on the branch yet, and no uncommitted changes';
  if (files > 0) return null;
  return scope === 'branch' ? 'No commits on the branch yet' : 'No changes yet';
}

export function ChangesPanel({ session, scope, onOpenDiff, uncommitted = false, landedSha = null, commit = null, projectId = null }: { session: Session; scope: DiffScope; onOpenDiff: () => void; uncommitted?: boolean; landedSha?: string | null; commit?: string | null; projectId?: string | null }) {
  const d = useDiff(session, scope, commit, projectId);
  const r = d.data;
  // QA-54: after landing, the worktree is gone; what landed is the commit on main.
  if (landedSha && scope !== 'commit' && d.error?.code === 'no-folder') return <ChangesPanel session={session} scope="commit" commit={landedSha} projectId={projectId} onOpenDiff={onOpenDiff} />;
  // No commits on the branch: show what the folder holds uncommitted instead (its own diff, scope working).
  if (scope === 'branch' && r && r.totals.files === 0 && !d.loading) return <ChangesPanel session={session} scope="working" onOpenDiff={onOpenDiff} uncommitted />;
  const files = r ? [...r.files].sort((a, b) => b.additions + b.deletions - (a.additions + a.deletions)) : [];
  return (
    <section className="flex flex-col gap-3" aria-label="Files changed">
      <RailHeading
        icon={FileDiff}
        title="Files changed"
        count={r ? (r.totals.files ? `${formatInt(r.totals.files)}` : undefined) : undefined}
        action={r && r.totals.files > 0 ? <IconButton label="Show the diff" icon={<SquareArrowOutUpRight />} size="xs" onClick={onOpenDiff} /> : undefined}
      />
      {d.loading ? (
        <div className="flex flex-col gap-2" role="status" aria-label="Loading the changes">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-48" />
          <Skeleton className="h-3 w-40" />
        </div>
      ) : d.error && !r ? (
        <p className="text-caption text-fg-subtle">
          {d.error.what}{' '}
          <Pressable className="rounded-control text-fg-muted underline-offset-2 hover:text-fg hover:underline" onClick={d.reload}>
            Try again
          </Pressable>
        </p>
      ) : !r || r.totals.files === 0 ? (
        <p className="text-caption text-fg-subtle">{changesLine(scope, 0, uncommitted)}</p>
      ) : (
        <>
          {uncommitted && <p className="text-caption text-fg-muted">{changesLine(scope, r.totals.files, true)}</p>}
          <p className="flex items-center gap-3 text-caption num">
            <span className="text-status-success">+{formatInt(r.totals.additions)}</span>
            <span className="text-status-danger">-{formatInt(r.totals.deletions)}</span>
            {r.commits.length > 0 && <span className="text-fg-subtle">{formatInt(r.commits.length + r.commitsOmitted)} commits</span>}
          </p>
          <ul className="flex flex-col">
            {files.slice(0, TOP_FILES).map((f) => (
              <li key={f.path} className="flex h-8 min-w-0 items-center gap-3">
                <span className="min-w-0 flex-1 truncate font-mono text-caption text-fg-muted" title={f.path}>
                  {shortPath(f.path, 44)}
                </span>
                <span className="w-10 shrink-0 text-right text-caption text-fg-subtle num">+{formatInt(f.additions)}</span>
                <span className="w-10 shrink-0 text-right text-caption text-fg-subtle num">-{formatInt(f.deletions)}</span>
              </li>
            ))}
          </ul>
          {files.length > TOP_FILES && (
            <Pressable className="w-fit rounded-control text-caption text-fg-subtle hover:text-fg" onClick={onOpenDiff}>
              {formatInt(files.length - TOP_FILES)} more files
            </Pressable>
          )}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- checks

const EVENT_ICON: Record<LandingEventTone, Icon> = { info: Info, ok: Check, warn: TriangleAlert, error: CircleX };
const EVENT_FG: Record<LandingEventTone, string> = { info: 'text-fg-subtle', ok: 'text-fg-subtle', warn: 'text-status-attention', error: 'text-status-danger' };

/** A step the queue has moved past reads in the past tense: "Running npm test" becomes "Ran npm test". */
export function stepText(text: string, past: boolean): string {
  return past ? text.replace(/^Running\b/, 'Ran') : text;
}

/** The landing queue's checks for the branch: its state, the last few steps, and the log one click away. */
export function ChecksPanel({ landing }: { landing: Landing | null }) {
  const status = landing
    ? landing.status === 'landed' || landing.stage === 'push' || landing.stage === 'deploy'
      ? 'Passed'
      : landing.status === 'failed'
        ? 'Failed'
        : landing.status === 'parked'
          ? 'Needs you'
          : landing.status === 'cancelled'
            ? 'Cancelled'
            : landing.status === 'queued'
              ? 'Queued'
              : 'Running'
    : null;
  return (
    <section className="flex flex-col gap-3" aria-label="Checks">
      <RailHeading
        icon={ShieldCheck}
        title="Checks"
        count={status ?? undefined}
        action={
          landing ? (
            <Link to={`/pipelines/landing?queue=${encodeURIComponent(landing.queueId)}&landing=${encodeURIComponent(landing.id)}`} aria-label="Open the landing in Pipelines" className="inline-flex size-6 items-center justify-center rounded-control text-fg-subtle hover:bg-hover hover:text-fg">
              <ExternalLink className="size-4" aria-hidden />
            </Link>
          ) : undefined
        }
      />
      {!landing ? (
        <p className="text-caption text-fg-subtle">They run when the branch goes to the landing queue</p>
      ) : (
        <>
          <ul className="flex flex-col gap-2">
            {landing.events.slice(-4).map((e, i, shown) => {
              const I = EVENT_ICON[e.tone];
              // Past once a later step followed it, or the run settled.
              const past = i < shown.length - 1 || (status !== 'Running' && status !== 'Queued');
              return (
                <li key={`${e.at}-${i}`} className="flex min-w-0 items-start gap-2">
                  <span className={cn('flex h-5 shrink-0 items-center', EVENT_FG[e.tone])}>
                    <I className="size-4" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1 text-caption text-fg-muted">{stepText(e.text, past)}</span>
                  <RelativeTime ts={e.at} className="flex h-5 shrink-0 items-center text-caption text-fg-subtle" />
                </li>
              );
            })}
          </ul>
          {landing.verifyMs != null && <p className="text-caption text-fg-subtle num">Verify took {formatDuration(landing.verifyMs)}</p>}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- sessions and links

/** The mission's sessions, when it ran more than one: pick the one the stream follows. */
export function SessionsPanel({ sessions, focusId, onFocus }: { sessions: readonly MissionSessionView[]; focusId: string | null; onFocus: (id: string) => void }) {
  return (
    <section className="flex flex-col gap-2" aria-label="Sessions">
      <RailHeading icon={FolderGit2} title="Sessions" count={formatInt(sessions.length)} />
      <ul className="-mx-2 flex flex-col">
        {sessions.map((v) => {
          const title = v.session ? plainText(v.session.title.replace(/^\[msn_[^\]]+\]\s*/, '')).split(/\s+Goal:/)[0] || 'Session' : v.launch?.result === 'error' ? 'Did not start' : v.launch?.result === 'pending' ? 'Pending' : 'Starting';
          const on = v.id === focusId;
          return (
            <li key={v.id}>
              {/* The title whole on its own line (two at most), the status and the time under it. */}
              <Pressable
                className={cn('flex min-h-row-2line min-w-0 w-full flex-col justify-center gap-1 rounded-control px-2 py-2 text-left hover:bg-hover', on && 'selected-tint')}
                aria-current={on || undefined}
                disabled={!v.session}
                title={title}
                onClick={() => v.sessionId && onFocus(v.id)}
              >
                <span className="line-clamp-2 min-w-0 text-ui text-fg">{title}</span>
                <span className="flex min-w-0 items-center gap-2 text-caption">
                  {/* "Needs you" is the one label for work waiting on a person (content/needs-you-noun). */}
                  {v.state === 'waiting_for_human' ? <StatePill tone="attention" icon={Hand} label="Needs you" className="shrink-0" /> : <SessionStatePill state={v.state} className="shrink-0" />}
                  {v.session && <RelativeTime ts={v.session.lastActivity} className="ml-auto shrink-0 text-caption text-fg-subtle" />}
                </span>
              </Pressable>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function LinksPanel({ worktree, session, item, onOpenSession }: { worktree: string | null; session: Session | null; item: BacklogItem | null; onOpenSession: (() => void) | null }) {
  if (!worktree && !session && !item) return null;
  return (
    <section className="flex flex-col gap-2" aria-label="Links">
      <dl className="flex flex-col gap-3">
        {worktree && (
          <div className="flex min-w-0 flex-col gap-1">
            <dt className="text-caption text-fg-subtle">Worktree</dt>
            <dd className="flex min-w-0 items-center gap-1">
              <span className="min-w-0 flex-1 truncate font-mono text-caption text-fg-muted" title={worktree}>
                {shortPath(worktree, 48)}
              </span>
              <CopyButton text={worktree} label="Copy the worktree path" size="xs" />
            </dd>
          </div>
        )}
        {session && onOpenSession && (
          <div className="flex min-w-0 flex-col gap-1">
            <dt className="text-caption text-fg-subtle">Session</dt>
            <dd>
              <Pressable className="flex w-fit max-w-full items-center gap-1 rounded-control text-ui text-fg-muted hover:text-fg" onClick={onOpenSession}>
                <span className="truncate font-mono text-caption">{session.id.slice(0, 8)}</span>
                <SquareArrowOutUpRight className="size-3 shrink-0" aria-hidden />
              </Pressable>
            </dd>
          </div>
        )}
        {item && (
          <div className="flex min-w-0 flex-col gap-1">
            <dt className="text-caption text-fg-subtle">Item</dt>
            <dd>
              <Link to={`/missions?item=${encodeURIComponent(item.id)}`} className="flex w-fit max-w-full items-center gap-1 rounded-control text-ui text-fg-muted hover:text-fg">
                <span className="shrink-0 font-mono text-caption">{item.key}</span>
                <span className="truncate">{item.title}</span>
              </Link>
            </dd>
          </div>
        )}
      </dl>
    </section>
  );
}
